import Link from "next/link";
import { CheckCircle } from "lucide-react";

import { ProductImage } from "@/components/ui/ProductImage";
import ProductSkuDecoded from "@/components/product/ProductSkuDecoded";
import { cn } from "@/lib/utils";

import {
    Card,
} from "@/components/ui/card";

import type { Product } from "@/types/product";
import type { Category } from "@/types/category";


// ─── Types ────────────────────────────────────────────────────────────────────
type Props = {
    products: Product[];
    categories: Category[];
    showCategory?: boolean;
    showProductSkuDecoded?: boolean;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; bg: string }> = {
    redirect:         { label: "ریردایرکت", bg: "bg-blue-500"  },
    "noindex,follow": { label: "آرشیو شده", bg: "bg-gray-500"  },
    "410":            { label: "حذف شده",   bg: "bg-red-600"   },
};

function getStatus(product: Product) {
    if (product.published === 0)
        return { label: "پیش‌نویس", bg: "bg-amber-500" };
    if (product.robots && STATUS_CONFIG[product.robots])
        return STATUS_CONFIG[product.robots];
    return null;
}

function getHref(product: Product) {
    return product.published === 0
        ? `/dashboard/products/preview/${product.slug}`
        : `/${process.env.NEXT_PUBLIC_SHOP_PRODUCT_SINGLE}/${product.slug}`;
}

function getCategoryNames(categoryIds: string, categories: Category[]) {
    if (!categoryIds) return "";
    return categoryIds
        .split(",")
        .map((id) => categories.find((c) => c.id === parseInt(id))?.title ?? "")
        .filter(Boolean)
        .join(" ، ");
}

function getImageSrc(primaryImage?: string) {
    if (!primaryImage) {
        return "/images/no-image.png";
    }

    return `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_PRODUCT}/${
        primaryImage.split(",")[0]
    }.${process.env.NEXT_PUBLIC_STORAGE_EXTENSION_PICTURE}?w=200`;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ product }: { product: Product }) {
    const status = getStatus(product);
    if (!status) return null;

    return (
        <div
            className={cn(
                "absolute right-[-52px] top-[14px] z-10 rotate-45 px-14 py-1.5 text-xs text-white",
                status.bg
            )}
        >
            {status.label}
        </div>
    );
}

// ─── List Single Card ─────────────────────────────────────────────────────────────
function SingleColumnCard({
                              product,
                              categories,
                              showCategory,
                          }: {
    product: Product;
    categories: Category[];
    showCategory: boolean;
}) {
    return (
        <Card className="single-col-card overflow-hidden relative border-transparent hover:border-blue-600">
            <StatusBadge product={product} />

            <Link
                href={getHref(product)}
                title={product.name}
                className="sm:flex w-full flex-row items-stretch block"
                prefetch={true}
            >

                {/* ── تصویر ── */}
                <div className="flex shrink-0 items-center justify-center bg-muted/40 w-full sm:w-[300px]  p-3">
                    <ProductImage
                        src={getImageSrc(product.primary_image)}
                        alt={product.name}
                    />
                </div>

                {/* ── محتوا ── */}
                <div className="flex flex-1 flex-col gap-1.5 px-4 p-5">

                    {/* عنوان */}
                    <p className="text-xl font-bold leading-snug text-foreground line-clamp-2 mb-3">
                        {product.name}
                    </p>

                    {/* ویژگی‌ها */}
                    {product.feature && product.feature.length > 0 && (
                        <ul className="flex flex-col gap-[3px]">
                            {product.feature.slice(0, 2).map((f, i) => (
                                <li
                                    key={i}
                                    className="flex items-center gap-1.5 text-xs text-muted-foreground"
                                >
                                    <CheckCircle className="h-3 w-3 shrink-0 text-green-500" />
                                    <span className="line-clamp-1">{f.title}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* ── پنل SKU ─ */}
                    <div className="flex shrink-0 flex-col justify-between">
                        {/* Part Number */}
                        {product.sku && (
                            <p className="text-left text-lg ltr text-blue-600 uppercase">
                                P/N: {product.sku}
                            </p>
                        )}
                    </div>

                    {/* مقادیر decode شده SKU */}
                    <div className={'mt-2'}>
                        <ProductSkuDecoded sku={product.sku} titleHide={1} />
                    </div>


                    {/* دسته‌بندی */}
                    {showCategory && (
                        <p className="mt-auto text-muted-foreground">
                            <strong className="font-medium text-foreground">دسته‌بندی: </strong>
                            {getCategoryNames(product.category_id, categories)}
                        </p>
                    )}
                </div>

            </Link>
        </Card>
    );
}

// ─── Grid Card ────────────────────────────────────────────────────────
function MultiColumnCard({
                             product,
                             categories,
                             showCategory,
                         }: {
    product: Product;
    categories: Category[];
    showCategory: boolean;
}) {
    return (
        <Card className="multi-col-card overflow-hidden relative border-transparent hover:border-blue-600">
            <StatusBadge product={product} />

            <Link
                href={getHref(product)}
                title={product.name}
                className="flex h-full flex-col"
                prefetch={true}
            >

                {/* تصویر */}
                <div className="p-3">
                    <ProductImage
                        src={getImageSrc(product.primary_image)}
                        alt={product.name}
                    />
                </div>

                {/* محتوا */}
                <div className="flex flex-1 flex-col gap-2 px-4 pb-3 pt-1">
                    <p className="leading-snug text-foreground line-clamp-2 tracking-tight hover:line-clamp-3  text-gray-900 text-lg font-bold mb-3">
                        {product.name}
                    </p>

                    {/*{product.feature && product.feature.length > 0 && (*/}
                    {/*    <ul className="flex flex-col gap-1">*/}
                    {/*        {product.feature.slice(0, 2).map((f, i) => (*/}
                    {/*            <li*/}
                    {/*                key={i}*/}
                    {/*                className="flex items-center gap-1.5 text-sm text-muted-foreground"*/}
                    {/*            >*/}
                    {/*                <CheckCircle className="h-3 w-3 shrink-0 text-green-500" />*/}
                    {/*                {f.title}*/}
                    {/*            </li>*/}
                    {/*        ))}*/}
                    {/*    </ul>*/}
                    {/*)}*/}

                    {showCategory && (
                        <p className="mt-auto text-xs text-muted-foreground">
                            <strong className="font-medium text-foreground">دسته‌بندی: </strong>
                            {getCategoryNames(product.category_id, categories)}
                        </p>
                    )}
                </div>

                {/* Part Number */}
                {product.sku && (
                    <div className="w-full px-4 py-2">
                        <p className="text-gray-600 text-sm text-left ltr uppercase">
                            P/N: {product.sku}
                        </p>
                    </div>
                )}

            </Link>
        </Card>
    );
}

// ─── ProductGrid ──────────────────────────────────────────────────────────────
export default function ProductGrid({
                                        products,
                                        categories,
                                        showCategory = false,
                                        showProductSkuDecoded = false,
                                    }: Props) {
    return (
        <div
            className={cn(
                "mb-2 grid gap-2",
                showProductSkuDecoded
                    ? "grid-cols-1"
                    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            )}
        >
            {products.map((product) =>
                showProductSkuDecoded ? (
                    <SingleColumnCard
                        key={product.id}
                        product={product}
                        categories={categories}
                        showCategory={showCategory}
                    />
                ) : (
                    <MultiColumnCard
                        key={product.id}
                        product={product}
                        categories={categories}
                        showCategory={showCategory}
                    />
                )
            )}
        </div>
    );
}